// ManualMeterModels.tsx - Component for Manual Meters tab
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { FilterX, Search } from "lucide-react";
import { Droplets, Zap, Bolt } from "lucide-react";
import { GenericTable } from "@shared/components/GenericTable";
import { useDeleteMeterModel, useMeterModels } from "../../hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { PAGE_SIZE } from "@shared/utils/constants";
import { TableAction, TableColumn } from "@shared/types/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { useChoices } from "@features/mx/device-management/hooks";

interface MeterModelApiData {
  id: number;
  remote_utility_id: number;
  meterManufacturer: {
    id: number;
    name: string;
    createdUserRemoteName: string;
  };
  meterMake: string;
  meterModel: string;
  meterType: number;
  manufacturerName: string;
  meterTypeDisplay: string;
  description: string;
  utilityService: string;
  technicalSpecifications: any;
  isActive: boolean;
  createdDate: string;
  createdUserRemoteName: string;
  meterCount?: number;
}

interface MeterFilters {
  manufacturer: string;
  is_active: string;
}

interface ManualMeterModelsProps {
  onDelete?: (id: string) => void;
  disableDelete?: boolean;
}

export const ManualMeterModels: React.FC<ManualMeterModelsProps> = ({
  onDelete: externalOnDelete,
  disableDelete = true,
}) => {
  const navigate = useNavigate();
  const remoteUtilityId = getRemoteUtilityId();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL Params — single source of truth
  const currentPage = Number(searchParams.get("page") || "1");
  const currentSearch = decodeURIComponent(searchParams.get("search") || "");
  const currentManufacturer = searchParams.get("manufacturer") || "";
  const currentStatus = searchParams.get("is_active") || "";

  // UI State for pending edits
  const [pendingFilters, setPendingFilters] = useState<MeterFilters>({
    manufacturer: decodeURIComponent(currentManufacturer),
    is_active: decodeURIComponent(currentStatus),
  });

  // Sync pendingFilters when URL changes
  useEffect(() => {
    setPendingFilters((prev) => ({
      ...prev,
      manufacturer: decodeURIComponent(currentManufacturer),
      is_active: decodeURIComponent(currentStatus),
    }));
  }, [currentManufacturer, currentStatus]);

  // Build query params object for MANUAL meters (meter_type = 4)
  const queryParams = useMemo(
    () => ({
      remote_utility_id: remoteUtilityId,
      meter_type: 4, // Manual meters
      searchData: currentSearch,
      manufacturer: currentManufacturer,
      is_active: currentStatus,
      page: currentPage,
      limit: PAGE_SIZE,
    }),
    [
      remoteUtilityId,
      currentPage,
      currentSearch,
      currentManufacturer,
      currentStatus,
    ]
  );

  const handlePendingFilterChange = (
    key: keyof MeterFilters,
    value: string
  ) => {
    setPendingFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Use your existing API hook with updated params structure
  const { data: meterData, isLoading } = useMeterModels(queryParams);

  // Get unique manufacturers from data
  const manufacturers = useMemo(() => {
    if (!meterData?.results) return [];
    const uniqueManufacturers = Array.from(
      new Set(
        meterData.results
          .map((meter: MeterModelApiData) => meter.meterManufacturer?.name)
          .filter(Boolean)
      )
    ).map((name, index) => ({
      id: index,
      name: name as string,
    }));
    return uniqueManufacturers;
  }, [meterData?.results]);

  const deleteMeterModel = useDeleteMeterModel();

  // Apply filters handler
  const handleApplyFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...(pendingFilters.manufacturer && {
        manufacturer: pendingFilters.manufacturer,
      }),
      ...(pendingFilters.is_active && { is_active: pendingFilters.is_active }),
      page: 1,
    };

    setSearchParams(newParams);
  };

  // Reset filters handler
  const handleResetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    delete newParams.manufacturer;
    delete newParams.is_active;
    delete newParams.search;
    newParams.page = 1;

    setSearchParams(newParams);
  };

  // Search handler
  const handleSearch = (searchTerm: string) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...(currentManufacturer && { manufacturer: currentManufacturer }),
      ...(currentStatus && { is_active: currentStatus }),
    };
    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }
    setSearchParams(newParams);
  };

  // Page change handler
  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };

    setSearchParams(newParams);
  };

  // Navigation handlers
  const handleView = (meter: MeterModelApiData) => {
    try {
      navigate(`/mx/manufacturer/view/${meter.id}`);
    } catch (error) {
      console.error("Error navigating to view:", error);
    }
  };

  const handleEdit = (meter: MeterModelApiData) => {
    try {
      navigate(`/mx/manufacturer/edit/${meter.id}`);
    } catch (error) {
      console.error("Error navigating to edit:", error);
    }
  };

  const handleDelete = async (meter: MeterModelApiData) => {
    // Prevent delete if disabled
    if (disableDelete) {
      console.log("Delete action is disabled");
      return;
    }

    try {
      if (externalOnDelete) {
        await deleteMeterModel.mutateAsync(meter.id.toString());
        externalOnDelete(meter.id.toString());
      }
    } catch (error) {
      console.error("Error deleting meter:", error);
    }
  };

  // Pagination object
  const pagination = meterData
    ? {
        currentPage,
        totalPages: Math.ceil(meterData.count / PAGE_SIZE),
        totalItems: meterData.count,
        startItem: (currentPage - 1) * PAGE_SIZE + 1,
        endItem: Math.min(currentPage * PAGE_SIZE, meterData.count),
        itemsPerPage: PAGE_SIZE,
      }
    : undefined;

  // Filter Panel Component
  const FilterPanel = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Manufacturer</label>
          <Select
            value={pendingFilters.manufacturer}
            onValueChange={(value) =>
              handlePendingFilterChange("manufacturer", value)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select Manufacturer" />
            </SelectTrigger>
            <SelectContent>
              {manufacturers && manufacturers.length > 0 ? (
                manufacturers.map((manufacturer) => (
                  <SelectItem key={manufacturer.id} value={manufacturer.name}>
                    {manufacturer.name}
                  </SelectItem>
                ))
              ) : (
                <SelectItem disabled value="no-data">
                  No data available
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button variant="default" onClick={handleApplyFilters}>
          Apply Filters
        </Button>
        <Button variant="ghost" onClick={handleResetFilters}>
          <FilterX className="w-4 h-4 mr-1" /> Clear
        </Button>
      </div>
    </div>
  );

  // Define table columns
  const columns: TableColumn<MeterModelApiData>[] = [
    {
      key: "manufacturer",
      header: "Manufacturer",
      sortable: true,
      render: (value: string | null, row: MeterModelApiData) =>
        row?.meterManufacturer?.name || "N/A",
    },
    {
      key: "modelName",
      header: "Model Name",
      sortable: true,
      render: (value: string, row: MeterModelApiData) => (
        <button
          onClick={() => handleView(row)}
          className="text-blue-600 hover:text-blue-800 hover:underline font-medium transition-colors"
        >
          {row.meterModel}
        </button>
      ),
    },
    {
      key: "meterType",
      header: "Meter Type",
      type: "badge" as const,
      render: (value: string, row: MeterModelApiData) => (
        <Badge variant="outline">
          {row.meterTypeDisplay || "Manual Meter"}
        </Badge>
      ),
    },
    {
      key: "totalMeters",
      header: "Total Meters",
      sortable: true,
      render: (value: number, row: MeterModelApiData) =>
        row.meterCount?.toLocaleString() || "0",
    },
    {
      key: "utilities",
      header: "Utilities",
      render: (value: string, row: MeterModelApiData) => {
        const utilityService = row.utilityService;
        if (!utilityService) return <span>N/A</span>;

        return (
          <div className="flex items-center gap-1">
            {utilityService.toLowerCase().includes("water") && (
              <Droplets className="h-4 w-4 text-blue-500" />
            )}
            {utilityService.toLowerCase().includes("gas") && (
              <Zap className="h-4 w-4 text-orange-500" />
            )}
            {utilityService.toLowerCase().includes("electric") && (
              <Bolt className="h-4 w-4 text-yellow-500" />
            )}
            <span>{utilityService}</span>
          </div>
        );
      },
    },
    {
      key: "createdBy",
      header: "Created By",
      render: (value: string, row: MeterModelApiData) =>
        row.createdUserRemoteName || "N/A",
    },
    {
      key: "createdOn",
      header: "Created On",
      sortable: true,
      render: (value: string, row: MeterModelApiData) =>
        new Date(row.createdDate).toLocaleDateString(),
    },
  ];

  // Define table actions - conditionally include delete action
  const actions: TableAction[] = [
    {
      label: "View",
      icon: "Eye",
      onClick: handleView,
    },
    {
      label: "Edit",
      icon: "Pencil",
      onClick: handleEdit,
    },
  ];

  // Check if filters are active
  const hasActiveFilters =
    currentManufacturer !== "" || currentStatus !== "";

  return (
    <div className="space-y-4">
      <GenericTable
        data={meterData?.results ?? []}
        columns={columns}
        actions={actions}
        actionsType="icons"
        rowKey="id"
        pagination={pagination}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchPlaceholder="Search manual meters..."
        searchDebounceMs={500}
        filterPanel={FilterPanel}
        emptyMessage="No manual meter models found matching your criteria"
        emptyIcon={<Search className="h-8 w-8 text-muted-foreground mb-2" />}
        filters={
          hasActiveFilters ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Filters active
              </span>
            </div>
          ) : null
        }
      />
    </div>
  );
};